import {
  DocumentBadgePlusIcon,
  EyeIcon,
  Table,
  Typography,
  useFetchFile,
  UserIcon,
  UserInfoIcon,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import { AdminJobSeekerType, AuthAccountContactTypeChoices } from "@graphql/generated";
import { Button, Spinner, Tooltip, User } from "@heroui/react";
import { formatString } from "@utils/formatString";
import React, { useMemo } from "react";

interface JobSeekerListTableProps {
  jobSeekers?: AdminJobSeekerType[];
  onClickJobSeekerCard: (id: string) => void;
  onClickSuggestionBtn?: (id: string) => void;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  loading: boolean;
  handleLoginAsJobSeeker?: (id: string) => void;
  handleViewJobSeekerFullDetails?: (id: string) => void;
}

const JobSeekerListTable: React.FC<JobSeekerListTableProps> = ({
  jobSeekers,
  onClickJobSeekerCard,
  onClickSuggestionBtn,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading,
  handleLoginAsJobSeeker,
  handleViewJobSeekerFullDetails,
}) => {
  const columns = useMemo(() => {
    return [
      {
        header: "No.",
        cell: (data: { row: { index: number } }) => {
          const rowIndex = data.row.index;
          return <Typography className="text-sm">{currentPage * pageSize - pageSize + rowIndex + 1}</Typography>;
        },
      },
      {
        header: "Name",
        cell: (data: { row: { original: AdminJobSeekerType } }) => {
          const jobSeeker = data.row.original as AdminJobSeekerType;
          return <NameCell jobSeeker={jobSeeker} />;
        },
      },
      {
        header: "Email/Phone",
        cell: (data: { row: { original: AdminJobSeekerType } }) => {
          const jobSeeker = data.row.original as AdminJobSeekerType;
          return <EmailCell jobSeeker={jobSeeker} />;
        },
      },
      {
        header: "Location",
        cell: (data: { row: { original: AdminJobSeekerType } }) => {
          const jobSeeker = data.row.original as AdminJobSeekerType;
          return <LocationCell jobSeeker={jobSeeker} />;
        },
      },
      {
        header: "Industry",
        cell: (data: { row: { original: AdminJobSeekerType } }) => {
          const jobSeeker = data.row.original as AdminJobSeekerType;
          return <IndustryCell jobSeeker={jobSeeker} />;
        },
      },
      {
        header: "Canada Visa",
        cell: (data: { row: { original: AdminJobSeekerType } }) => {
          const jobSeeker = data.row.original as AdminJobSeekerType;
          return <CanadaVisaCell jobSeeker={jobSeeker} />;
        },
      },
      {
        header: "Action",
        cell: (data: { row: { original: AdminJobSeekerType } }) => {
          const jobSeeker = data.row.original as AdminJobSeekerType;
          return (
            <ActionCell
              jobSeeker={jobSeeker}
              onClickJobSeekerCard={onClickJobSeekerCard}
              onClickSuggestionBtn={onClickSuggestionBtn}
              handleLoginAsJobSeeker={handleLoginAsJobSeeker}
              handleViewJobSeekerFullDetails={handleViewJobSeekerFullDetails}
            />
          );
        },
      },
    ];
  }, [currentPage, onClickJobSeekerCard, onClickSuggestionBtn, pageSize]);

  return (
    <Table
      isLoading={loading}
      columns={columns}
      data={jobSeekers || []}
      bottomContentCustomComponent={<></>}
      enablePagination={!!totalCount}
      totalItems={totalCount || 0}
      currentPage={currentPage || 0}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onRowClick={(row) => onClickJobSeekerCard(row.id)}
      classNames={{
        tr: "cursor-pointer",
      }}
    />
  );
};

export default JobSeekerListTable;

const NameCell: React.FC<{ jobSeeker: AdminJobSeekerType }> = ({ jobSeeker }) => {
  const { data: avatarUrl, loading } = useFetchFile({
    urlPath: jobSeeker?.profile?.avatar?.file,
    cache: "no-cache",
    userType: UserTypeEnum.ADMIN,
  });

  const fullName = `${jobSeeker?.firstName || jobSeeker?.lastName ? `${jobSeeker?.firstName} ${jobSeeker?.lastName}` : "-"}`;
  const truncatedName = fullName.length > 20 ? `${fullName.substring(0, 20)}...` : fullName;

  return (
    <User
      name={truncatedName}
      description={formatString(jobSeeker?.profile?.gender || "-")}
      avatarProps={{
        src: jobSeeker?.profile?.avatar && !loading ? avatarUrl : "",
        isDisabled: !jobSeeker?.profile?.avatar,
        fallback: loading ? <Spinner size="sm" /> : undefined,
        classNames: {
          base: "w-10 h-10 min-w-10 min-h-10",
          img: "opacity-1",
        },
      }}
    />
  );
};

const EmailCell: React.FC<{ jobSeeker: AdminJobSeekerType }> = ({ jobSeeker }) => {
  return (
    <div className="flex flex-col gap-1">
      <Typography className="text-sm">{jobSeeker?.email || "-"}</Typography>
      <Typography className="text-sm">
        {jobSeeker?.profile?.contacts?.find((contact) => contact?.type === AuthAccountContactTypeChoices.PHONE)
          ?.value || "-"}
      </Typography>
    </div>
  );
};

const LocationCell: React.FC<{ jobSeeker: AdminJobSeekerType }> = ({ jobSeeker }) => {
  return (
    <>
      {jobSeeker?.profile?.city?.name && jobSeeker?.profile?.city?.country?.name && (
        <Typography className="text-sm">
          {jobSeeker?.profile?.city?.name}, {jobSeeker?.profile?.city?.country?.name}
        </Typography>
      )}
      {!jobSeeker?.profile?.city?.name && !jobSeeker?.profile?.city?.country?.name && (
        <Typography className="text-sm">-</Typography>
      )}
      {jobSeeker?.profile?.city?.name && !jobSeeker?.profile?.city?.country?.name && (
        <Typography className="text-sm">{jobSeeker?.profile?.city?.name}</Typography>
      )}
      {!jobSeeker?.profile?.city?.name && jobSeeker?.profile?.city?.country?.name && (
        <Typography className="text-sm">{jobSeeker?.profile?.city?.country?.name}</Typography>
      )}
    </>
  );
};

const IndustryCell: React.FC<{ jobSeeker: AdminJobSeekerType }> = ({ jobSeeker }) => {
  return <Typography className="text-sm">{jobSeeker?.profile?.industry?.title || "-"}</Typography>;
};

const CanadaVisaCell: React.FC<{ jobSeeker: AdminJobSeekerType }> = ({ jobSeeker }) => {
  return <Typography className="text-sm">{formatString(jobSeeker?.canadaVisa?.status || "-")}</Typography>;
};

const ActionCell: React.FC<{
  jobSeeker: AdminJobSeekerType;
  onClickJobSeekerCard: (id: string) => void;
  onClickSuggestionBtn?: (id: string) => void;
  handleLoginAsJobSeeker?: (id: string) => void;
  handleViewJobSeekerFullDetails?: (id: string) => void;
}> = ({
  jobSeeker,
  onClickJobSeekerCard,
  onClickSuggestionBtn,
  handleLoginAsJobSeeker,
  handleViewJobSeekerFullDetails,
}) => {
  return (
    <div className="flex gap-2">
      {handleViewJobSeekerFullDetails && (
        <Tooltip content="View Job Seeker Full Details">
          <Button
            isIconOnly
            variant="light"
            className="p-1 h-auto min-w-0 w-auto"
            onPress={() => handleViewJobSeekerFullDetails?.(jobSeeker?.id)}
          >
            <UserInfoIcon className="cursor-pointer w-5 h-5 stroke-gray-500 hover:stroke-gray-700" />
          </Button>
        </Tooltip>
      )}

      <Tooltip content="View Job Seeker">
        <Button
          isIconOnly
          variant="light"
          className="p-1 h-auto min-w-0 w-auto"
          onPress={() => onClickJobSeekerCard(jobSeeker?.id)}
        >
          <EyeIcon className="cursor-pointer w-5 h-5 stroke-gray-500 hover:stroke-gray-700" />
        </Button>
      </Tooltip>

      {handleLoginAsJobSeeker && (
        <Tooltip content="Login as Job Seeker">
          <Button
            isIconOnly
            variant="light"
            className="p-1 h-auto min-w-0 w-auto"
            onPress={() => handleLoginAsJobSeeker(jobSeeker?.id)}
          >
            <UserIcon className="cursor-pointer w-5 h-5 stroke-gray-500 hover:stroke-gray-700" />
          </Button>
        </Tooltip>
      )}
      {onClickSuggestionBtn && (
        <Tooltip content="Assign Job Seeker">
          <Button
            isIconOnly
            variant="light"
            className="p-1 h-auto min-w-0 w-auto"
            onPress={() => onClickSuggestionBtn(jobSeeker?.id)}
          >
            <DocumentBadgePlusIcon className="cursor-pointer w-5 h-5 stroke-gray-500 hover:stroke-gray-700" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};
